import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchTrucks, fetchStats, createTruck, checkPlate, api, uploadPhoto } from "@/lib/api";
import { useSites } from "@/lib/sites";
import { useAuth, filterSitesForUser } from "@/lib/auth";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import ExcelJS from "exceljs";
import { toast } from "sonner";
import {
  Plus,
  Truck as TruckIcon,
  WarningCircle,
  CheckCircle,
  Pause,
  ArrowSquareOut,
  MagnifyingGlass,
  FunnelSimple,
  X,
  Users,
  Wrench,
  MapPin,
  Warning,
  FileXls,
} from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_LABEL = {
  actif: { label: "Actif", cls: "status-actif" },
  inactif: { label: "Inactif", cls: "status-inactif" },
  rendu: { label: "Rendu", cls: "status-rendu" },
  panne: { label: "En panne", cls: "status-panne" },
};

const SITE_LABEL = {
  DLP4: "Perpignan DLP4",
  DWB9: "Rennes DWB9",
};
// Legacy in-file fallback. When possible, use useSites() and read code/name dynamically.
const siteLabelDyn = (code, sites) => {
  if (!code) return "";
  const s = (sites || []).find((x) => x.code === code);
  if (s) return s.name ? `${s.code} · ${s.name}` : s.code;
  return SITE_LABEL[code] || code;
};

const RENTAL_LABEL = {
  own: "NY TRANS",
  "loc-eco": "Loc Eco",
  europcar: "Europcar",
  hertz: "Hertz",
  ada: "ADA",
  loa: "LOA (Leasing)",
  autre: "Autre",
};

const ownerLabel = (truck, ownerMap = {}) => {
  if (!truck) return "—";
  const rt = truck.rental_type;
  if (!rt) return "—";
  if (rt === "autre") return truck.rental_type_custom || "Autre";
  return ownerMap[rt] || RENTAL_LABEL[rt] || rt;
};

function StatCard({ label, value, accent, testId }) {
  return (
    <div
      className="bg-white border border-zinc-900 p-5 brutalist-shadow-sm"
      data-testid={testId}
    >
      <div className="label-tag">{label}</div>
      <div
        className="text-4xl md:text-5xl font-black tracking-tighter mt-2"
        style={{ fontFamily: "Chivo", color: accent || "#09090B" }}
      >
        {value}
      </div>
    </div>
  );
}

function TruckRow({ truck, onOpen, agg, ownerMap }) {
  const status = STATUS_LABEL[truck.status] || STATUS_LABEL.actif;
  const owner = ownerLabel(truck, ownerMap);
  return (
    <div
      className="grid grid-cols-12 gap-3 px-4 py-4 bg-white border-b border-zinc-200 hover:bg-zinc-50 cursor-pointer transition-none items-center"
      onClick={onOpen}
      data-testid={`truck-row-${truck.id}`}
    >
      <div className="col-span-12 md:col-span-3 flex items-center gap-3">
        <div className="bg-zinc-900 text-white p-2">
          <TruckIcon size={18} weight="bold" />
        </div>
        <div>
          <div className="font-bold text-base mono uppercase">
            {truck.license_plate || "—"}
          </div>
          <div className="text-xs text-zinc-500">
            {truck.brand} {truck.model} {truck.year ? `· ${truck.year}` : ""}
          </div>
        </div>
      </div>
      <div className="col-span-6 md:col-span-2">
        <div className="label-tag">Type</div>
        <div className="text-sm font-semibold uppercase">{truck.van_type}</div>
      </div>
      <div className="col-span-6 md:col-span-2">
        <div className="label-tag">Propriété</div>
        <div
          className="text-sm font-bold uppercase truncate"
          data-testid={`truck-owner-${truck.id}`}
          title={owner}
        >
          {owner}
        </div>
      </div>
      <div className="col-span-6 md:col-span-2">
        <div className="label-tag">Kilométrage</div>
        <div className="text-sm mono">{truck.mileage ? truck.mileage.toLocaleString() + " km" : "—"}</div>
      </div>
      <div className="col-span-6 md:col-span-2 flex flex-wrap gap-1.5">
        <span
          className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${status.cls}`}
          data-testid={`truck-status-${truck.id}`}
        >
          {status.label}
        </span>
        {truck.status === "panne" && truck.panne_severity && (
          <span
            className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border ${
              truck.panne_severity === "majeur"
                ? "bg-red-600 text-white border-red-700"
                : "bg-amber-500 text-white border-amber-600"
            }`}
            data-testid={`truck-panne-sev-${truck.id}`}
            title={truck.panne_reason || ""}
          >
            {truck.panne_severity === "majeur" ? "MAJEURE" : "MINEURE"}
          </span>
        )}
        {truck.status === "panne" && truck.panne_reason && (
          <span
            className="px-2 py-1 text-[10px] font-medium normal-case tracking-normal bg-zinc-100 text-zinc-700 border border-zinc-300 truncate max-w-[260px]"
            data-testid={`truck-panne-reason-${truck.id}`}
            title={truck.panne_reason}
          >
            {truck.panne_reason}
          </span>
        )}
        {truck.status === "panne" && (() => {
          const isRented = (truck.rental_type || "own") !== "own";
          const daysBroken = truck.panne_date
            ? Math.max(0, Math.floor((Date.now() - new Date(truck.panne_date).getTime()) / (1000 * 3600 * 24)) + 1)
            : 0;
          const dailyRev = truck.daily_revenue != null ? truck.daily_revenue : 200;
          const revenueLoss = dailyRev * daysBroken;
          // Use the current month's actual length so this matches /api/budget/overview
          const now = new Date();
          const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
          const rentDaily = isRented && truck.monthly_rent ? (truck.monthly_rent / daysInMonth) : 0;
          const rentLoss = (isRented && !truck.rental_frozen ? rentDaily * daysBroken : 0);
          const totalLoss = revenueLoss + rentLoss;
          if (totalLoss <= 0) return null;
          const needsFreeze = isRented && !truck.rental_frozen;
          return (
            <span
              className={`px-2 py-1 text-[10px] font-bold uppercase tracking-widest border ${
                needsFreeze
                  ? "bg-red-600 text-white border-red-700"
                  : "bg-zinc-100 text-red-700 border-red-400"
              }`}
              data-testid={`truck-loss-${truck.id}`}
              title={needsFreeze ? "Location non gelée · Contacter l'agence" : "Manque à gagner estimé"}
              onClick={(e) => e.stopPropagation()}
            >
              {needsFreeze ? "⚠ " : ""}
              −{totalLoss.toLocaleString("fr-FR", { maximumFractionDigits: 0 })}€
              {daysBroken > 0 && ` · ${daysBroken}j`}
            </span>
          );
        })()}
        {truck.red_tag && (
          <span
            className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest status-red-tag"
            data-testid={`truck-redtag-${truck.id}`}
          >
            RED TAG
          </span>
        )}
        {truck.awaiting_expert && (
          <span
            className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest status-expert"
            data-testid={`truck-expert-${truck.id}`}
          >
            Att. expert
          </span>
        )}
        {agg && agg.count > 0 && (
          <span
            className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-zinc-100 border border-zinc-300 mono"
            data-testid={`truck-impacts-${truck.id}`}
          >
            {agg.count} imp · {agg.total_cost.toLocaleString()}€
          </span>
        )}
      </div>
      <div className="col-span-12 md:col-span-1 flex justify-end">
        <ArrowSquareOut size={20} weight="bold" />
      </div>
    </div>
  );
}

// ============================================================
// Export Excel — full fleet snapshot, one styled sheet per site
// ============================================================
function ExportFleetExcelButton() {
  const [busy, setBusy] = useState(false);

  const STATUS_LABELS = {
    actif: "Actif",
    inactif: "Inactif",
    rendu: "Rendu",
    panne: "En panne",
    red_tag: "Red Tag",
    attente_expertise: "Attente expertise",
  };
  const STATUS_COLORS = {
    actif: { bg: "FF16A34A", fg: "FFFFFFFF" },        // green
    panne: { bg: "FFDC2626", fg: "FFFFFFFF" },        // red
    red_tag: { bg: "FFB91C1C", fg: "FFFFFFFF" },      // dark red
    attente_expertise: { bg: "FF9333EA", fg: "FFFFFFFF" }, // purple
    inactif: { bg: "FF6B7280", fg: "FFFFFFFF" },      // gray
    rendu: { bg: "FF2563EB", fg: "FFFFFFFF" },        // blue
  };
  const STATUS_ORDER = ["actif", "panne", "red_tag", "attente_expertise", "inactif", "rendu"];
  const SEVERITY_LABELS = {
    mineure: "Mineure",
    modere: "Modérée",
    majeure: "Majeure",
    critique: "Critique",
  };
  const SEVERITY_ALIASES = { mineur: "mineure", majeur: "majeure", moderee: "modere", "modérée": "modere" };
  const severityLabel = (v) => SEVERITY_LABELS[SEVERITY_ALIASES[v] || v] || v || "";
  const SEVERITY_COLORS = {
    mineure: "FFFDE68A",   // yellow-200
    modere: "FFFDBA74",    // orange-300
    majeure: "FFF87171",   // red-400
    critique: "FF991B1B",  // red-800
  };

  // Design tokens
  const GOLD = "FFC7A15A";       // NY gold
  const DARK = "FF111827";       // near-black
  const ZEBRA = "FFF9FAFB";      // zinc-50
  const HEADER_BG = "FF1F2937";  // slate-800
  const SECTION_BG = "FFFEF3C7"; // amber-100

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return String(iso).slice(0, 10); }
  };

  const setBorders = (cell) => {
    cell.border = {
      top: { style: "thin", color: { argb: "FFE5E7EB" } },
      left: { style: "thin", color: { argb: "FFE5E7EB" } },
      right: { style: "thin", color: { argb: "FFE5E7EB" } },
      bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
    };
  };

  const styleTitle = (ws, text) => {
    const row = ws.addRow([text]);
    row.height = 30;
    ws.mergeCells(row.number, 1, row.number, 12);
    const cell = row.getCell(1);
    cell.font = { name: "Calibri", size: 16, bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: DARK } };
    cell.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
  };

  const styleSubtitle = (ws, text) => {
    const row = ws.addRow([text]);
    row.height = 18;
    ws.mergeCells(row.number, 1, row.number, 12);
    const cell = row.getCell(1);
    cell.font = { name: "Calibri", size: 10, italic: true, color: { argb: "FF6B7280" } };
    cell.alignment = { horizontal: "left", indent: 1 };
  };

  const styleSectionHeader = (ws, text, span = 12) => {
    const row = ws.addRow([text]);
    row.height = 24;
    ws.mergeCells(row.number, 1, row.number, span);
    const cell = row.getCell(1);
    cell.font = { name: "Calibri", size: 11, bold: true, color: { argb: DARK } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: GOLD } };
    cell.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
    cell.border = {
      bottom: { style: "medium", color: { argb: DARK } },
    };
  };

  const styleColumnHeaders = (ws, headers) => {
    const row = ws.addRow(headers);
    row.height = 26;
    row.eachCell((cell) => {
      cell.font = { name: "Calibri", size: 10, bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_BG } };
      cell.alignment = { vertical: "middle", horizontal: "center", wrapText: false };
      cell.border = {
        top: { style: "thin", color: { argb: HEADER_BG } },
        bottom: { style: "medium", color: { argb: GOLD } },
        left: { style: "thin", color: { argb: HEADER_BG } },
        right: { style: "thin", color: { argb: HEADER_BG } },
      };
    });
    // NOTE: no ws.views frozen pane here — multiple section headers per sheet
    // would otherwise re-freeze on the last one, breaking scroll of prior data.
  };

  const styleDataRow = (row, opts = {}) => {
    const { zebra = false, statusCol = -1, severityCol = -1, moneyCols = [], centerCols = [] } = opts;
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = { name: "Calibri", size: 10, color: { argb: "FF111827" } };
      cell.alignment = {
        vertical: "middle",
        horizontal: centerCols.includes(colNumber) ? "center" : "left",
        wrapText: true,
      };
      setBorders(cell);
      if (zebra) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ZEBRA } };
      }
      // Status coloring
      if (colNumber === statusCol && typeof cell.value === "string") {
        const key = Object.entries(STATUS_LABELS).find(([, v]) => v === cell.value)?.[0];
        if (key && STATUS_COLORS[key]) {
          cell.font = { ...cell.font, bold: true, color: { argb: STATUS_COLORS[key].fg } };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: STATUS_COLORS[key].bg } };
          cell.alignment = { ...cell.alignment, horizontal: "center" };
        }
      }
      // Severity coloring
      if (colNumber === severityCol && typeof cell.value === "string" && cell.value) {
        const key = Object.entries(SEVERITY_LABELS).find(([, v]) => v === cell.value)?.[0];
        if (key && SEVERITY_COLORS[key]) {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: SEVERITY_COLORS[key] } };
          cell.font = { ...cell.font, bold: true };
          cell.alignment = { ...cell.alignment, horizontal: "center" };
        }
      }
      if (moneyCols.includes(colNumber) && typeof cell.value === "number") {
        cell.numFmt = '#,##0" €"';
        cell.alignment = { ...cell.alignment, horizontal: "right" };
      }
    });
  };

  const addEmptyRow = (ws) => ws.addRow([]);

  const exportExcel = async () => {
    setBusy(true);
    try {
      const [trucks, owners] = await Promise.all([
        api.get(`/trucks?site=ALL`).then((r) => r.data),
        api.get(`/owners`).then((r) => r.data).catch(() => []),
      ]);
      const ownerMap = Object.fromEntries(owners.map((o) => [o.key, o.label]));
      const ownerOf = (t) => { const l = ownerLabel(t, ownerMap); return l === "—" ? "" : l; };
      if (!trucks || trucks.length === 0) {
        toast.error("Aucun camion à exporter");
        return;
      }
      const bySite = {};
      trucks.forEach((t) => {
        const code = t.site || "AUTRE";
        (bySite[code] ||= []).push(t);
      });

      const wb = new ExcelJS.Workbook();
      wb.creator = "NY TRANS — Contrôle de flotte";
      wb.created = new Date();

      const today = new Date().toLocaleDateString("fr-FR");

      // ============ RÉSUMÉ GLOBAL ============
      const gs = wb.addWorksheet("Résumé global", {
        properties: { tabColor: { argb: GOLD } },
      });
      gs.columns = [
        { width: 22 }, { width: 12 },
        ...STATUS_ORDER.map(() => ({ width: 14 })),
      ];
      styleTitle(gs, "FLOTTE NY TRANS — SNAPSHOT FLOTTE");
      styleSubtitle(gs, `Export du ${today}  ·  ${trucks.length} camions au total`);
      addEmptyRow(gs);
      styleColumnHeaders(gs, ["Site", "Total", ...STATUS_ORDER.map((s) => STATUS_LABELS[s])]);
      const gsHeaderRow = gs.rowCount;
      Object.keys(bySite).sort().forEach((code, idx) => {
        const list = bySite[code];
        const row = gs.addRow([
          code,
          list.length,
          ...STATUS_ORDER.map((s) => list.filter((t) => t.status === s).length),
        ]);
        styleDataRow(row, { zebra: idx % 2 === 1, centerCols: [2, 3, 4, 5, 6, 7, 8] });
        row.getCell(1).font = { name: "Calibri", size: 11, bold: true, color: { argb: DARK } };
      });
      // Total row
      const totalRow = gs.addRow([
        "TOTAL",
        trucks.length,
        ...STATUS_ORDER.map((s) => trucks.filter((t) => t.status === s).length),
      ]);
      totalRow.eachCell((cell) => {
        cell.font = { name: "Calibri", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: DARK } };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        setBorders(cell);
      });
      totalRow.getCell(1).alignment = { vertical: "middle", horizontal: "left", indent: 1 };
      // small note at bottom of global sheet
      addEmptyRow(gs);
      const noteRow = gs.addRow(["Note : les colonnes correspondent au statut de chaque camion. Ouvre les onglets ci-dessous pour le détail par site."]);
      gs.mergeCells(noteRow.number, 1, noteRow.number, 8);
      noteRow.getCell(1).font = { italic: true, size: 9, color: { argb: "FF6B7280" } };
      void gsHeaderRow;

      // ============ ONGLETS PAR SITE ============
      Object.keys(bySite).sort().forEach((code) => {
        const list = bySite[code];
        const ws = wb.addWorksheet(code, {
          properties: { tabColor: { argb: list.some((t) => t.status === "panne") ? "FFDC2626" : GOLD } },
        });
        ws.columns = [
          { width: 15 },   // 1 Immat.
          { width: 14 },   // 2 Marque | Statut
          { width: 14 },   // 3 Modèle | Sévérité
          { width: 22 },   // 4 Type | Raison de la panne
          { width: 14 },   // 5 Année | Panne depuis
          { width: 16 },   // 6 Statut | Gel loyer (j)
          { width: 18 },   // 7 Sévérité panne | Agence prévenue
          { width: 30 },   // 8 Raison panne | Méthode
          { width: 22 },   // 9 Propriétaire | Note agence
          { width: 20 },   // 10 Loyer mensuel | Position actuelle
          { width: 18 },   // 11 Gel loyer (j) | GPS
          { width: 40 },   // 12 Position | -
        ];

        styleTitle(ws, `SITE ${code} — ${list.length} camion(s)`);
        styleSubtitle(ws, `Export du ${today}`);
        addEmptyRow(ws);

        // RÉCAPITULATIF
        styleSectionHeader(ws, "📊 RÉCAPITULATIF", 12);
        styleColumnHeaders(ws, ["Statut", "Nombre", "", "", "", "", "", "", "", "", "", ""]);
        STATUS_ORDER.forEach((s, i) => {
          const n = list.filter((t) => t.status === s).length;
          if (n > 0) {
            const row = ws.addRow([STATUS_LABELS[s], n, "", "", "", "", "", "", "", "", "", ""]);
            styleDataRow(row, { zebra: i % 2 === 1, statusCol: 1, centerCols: [2] });
          }
        });
        addEmptyRow(ws);

        // LISTE COMPLÈTE (rendus exclus par défaut, présentés en bas dans une section séparée)
        const activeList = list.filter((t) => t.status !== "rendu");
        const returnedList = list.filter((t) => t.status === "rendu");
        styleSectionHeader(ws, `🚚 LISTE COMPLÈTE DES CAMIONS (${activeList.length})`, 12);
        const listHeaderRowNumber = ws.rowCount + 1;
        styleColumnHeaders(ws, [
          "Immat.", "Marque", "Modèle", "Type", "Année", "Statut",
          "Sévérité panne", "Raison panne", "Propriétaire",
          "Loyer mensuel", "Gel loyer (j)", "Position",
        ]);
        const listStartDataRow = ws.rowCount + 1;
        activeList
          .slice()
          .sort((a, b) => {
            const rank = (t) => (t.status === "panne" ? 0 : t.status === "red_tag" ? 1 : 2);
            const dr = rank(a) - rank(b);
            if (dr !== 0) return dr;
            return (a.license_plate || "").localeCompare(b.license_plate || "");
          })
          .forEach((t, i) => {
            const row = ws.addRow([
              t.license_plate || "",
              t.brand || "",
              t.model || "",
              t.type || "",
              t.year || "",
              STATUS_LABELS[t.status] || t.status || "",
              t.status === "panne" ? severityLabel(t.panne_severity) : "",
              t.status === "panne" ? t.panne_reason || "" : "",
              ownerOf(t),
              (t.monthly_rent ?? t.rental_price) ? Number(t.monthly_rent ?? t.rental_price) : "",
              t.rental_freeze_days || "",
              t.location_address || "",
            ]);
            styleDataRow(row, {
              zebra: i % 2 === 1,
              statusCol: 6,
              severityCol: 7,
              moneyCols: [10],
              centerCols: [5, 11],
            });
            row.getCell(1).font = { name: "Calibri", size: 10, bold: true, color: { argb: DARK } };
          });
        const listEndDataRow = ws.rowCount;
        // Native Excel auto-filter on the "LISTE COMPLÈTE" header row so the user
        // can natively sort/filter the list by any column (statut, sévérité, …).
        if (listEndDataRow >= listStartDataRow) {
          ws.autoFilter = {
            from: { row: listHeaderRowNumber, column: 1 },
            to: { row: listEndDataRow, column: 12 },
          };
        }
        addEmptyRow(ws);

        // DÉTAIL EN PANNE
        const broken = list.filter((t) => t.status === "panne" || t.status === "red_tag");
        if (broken.length > 0) {
          styleSectionHeader(ws, `🚨 DÉTAIL DES CAMIONS EN PANNE / RED TAG (${broken.length})`, 12);
          styleColumnHeaders(ws, [
            "Immat.", "Statut", "Sévérité", "Raison de la panne",
            "Panne depuis", "Gel loyer (j)", "Agence prévenue",
            "Méthode", "Note agence", "Position actuelle", "GPS", "",
          ]);
          broken.forEach((t, i) => {
            const row = ws.addRow([
              t.license_plate || "",
              STATUS_LABELS[t.status],
              severityLabel(t.panne_severity),
              t.panne_reason || "",
              fmtDate(t.panne_since || t.panne_date),
              t.rental_freeze_days || 0,
              t.agency_notified ? "Oui" : "Non",
              t.agency_notified_method || "",
              t.agency_notified_note || "",
              t.location_address || "",
              t.location_lat && t.location_lng ? `${t.location_lat}, ${t.location_lng}` : "",
              "",
            ]);
            styleDataRow(row, {
              zebra: i % 2 === 1,
              statusCol: 2,
              severityCol: 3,
              centerCols: [5, 6, 7, 8],
            });
            row.getCell(1).font = { name: "Calibri", size: 10, bold: true, color: { argb: DARK } };
            // "Agence prévenue" colored yes/no
            const anCell = row.getCell(7);
            if (anCell.value === "Oui") {
              anCell.font = { ...anCell.font, bold: true, color: { argb: "FF16A34A" } };
            } else if (anCell.value === "Non") {
              anCell.font = { ...anCell.font, bold: true, color: { argb: "FFDC2626" } };
            }
          });
        }

        // ARCHIVES : CAMIONS RENDUS (masqués par défaut, présentés en fin d'onglet)
        if (returnedList.length > 0) {
          addEmptyRow(ws);
          styleSectionHeader(ws, `🗄️ CAMIONS RENDUS / ARCHIVÉS (${returnedList.length})`, 12);
          styleColumnHeaders(ws, [
            "Immat.", "Marque", "Modèle", "Type", "Année", "Statut",
            "", "", "Propriétaire",
            "Loyer mensuel", "", "Dernière position",
          ]);
          returnedList
            .slice()
            .sort((a, b) => (a.license_plate || "").localeCompare(b.license_plate || ""))
            .forEach((t, i) => {
              const row = ws.addRow([
                t.license_plate || "",
                t.brand || "",
                t.model || "",
                t.type || "",
                t.year || "",
                STATUS_LABELS[t.status] || t.status || "",
                "", "",
                ownerOf(t),
                (t.monthly_rent ?? t.rental_price) ? Number(t.monthly_rent ?? t.rental_price) : "",
                "",
                t.location_address || "",
              ]);
              styleDataRow(row, {
                zebra: i % 2 === 1,
                statusCol: 6,
                moneyCols: [10],
                centerCols: [5],
              });
              row.getCell(1).font = { name: "Calibri", size: 10, bold: true, color: { argb: DARK } };
              // Grey-out returned trucks visually
              row.eachCell({ includeEmpty: true }, (cell) => {
                if (!cell.fill || !cell.fill.fgColor || cell.fill.fgColor.argb === ZEBRA) {
                  cell.font = { ...cell.font, color: { argb: "FF6B7280" } };
                }
              });
            });
        }
      });

      // Trigger download
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flotte-nytrans-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Export Excel généré");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Erreur export");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={exportExcel}
      disabled={busy}
      className="bg-white text-zinc-900 px-4 py-3 font-bold uppercase tracking-widest text-xs hover:bg-zinc-100 border border-zinc-900 inline-flex items-center gap-2 disabled:opacity-50"
      data-testid="export-fleet-excel-btn"
      title="Exporter la flotte complète en Excel"
    >
      <FileXls size={16} weight="bold" />
      {busy ? "…" : "Excel"}
    </button>
  );
}



function AddTruckDialog({ onCreated, currentSite, owners, refreshOwners }) {
  const { sites: sitesList } = useSites();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showNewOwner, setShowNewOwner] = useState(false);
  const [newOwnerLabel, setNewOwnerLabel] = useState("");
  const [plateCheck, setPlateCheck] = useState({ exact: [], similar: [], loading: false });
  const [extracting, setExtracting] = useState(false);
  const [extractionInfo, setExtractionInfo] = useState("");
  const [form, setForm] = useState({
    license_plate: "",
    brand: "",
    model: "",
    year: "",
    mileage: "",
    van_type: "trafic",
    site: currentSite || "DLP4",
    rental_type: "own",
    rental_type_custom: "",
    monthly_rent: "",
    daily_revenue: "200",
    rental_agency: "",
    vin: "",
    carte_grise_doc: "",
  });

  // Debounced live plate check
  useEffect(() => {
    const plate = form.license_plate.trim();
    if (!plate || plate.length < 2 || !open) {
      setPlateCheck({ exact: [], similar: [], loading: false });
      return;
    }
    setPlateCheck((p) => ({ ...p, loading: true }));
    const timer = setTimeout(async () => {
      try {
        const res = await checkPlate(plate);
        setPlateCheck({ exact: res.exact || [], similar: res.similar || [], loading: false });
      } catch {
        setPlateCheck({ exact: [], similar: [], loading: false });
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [form.license_plate, open]);

  const reset = () => {
    setForm({
      license_plate: "",
      brand: "",
      model: "",
      year: "",
      mileage: "",
      van_type: "trafic",
      site: currentSite || "DLP4",
      rental_type: "own",
      rental_type_custom: "",
      monthly_rent: "",
      daily_revenue: "200",
      rental_agency: "",
      vin: "",
      carte_grise_doc: "",
    });
    setExtractionInfo("");
  };

  const handleAddOwner = async () => {
    const lbl = newOwnerLabel.trim();
    if (!lbl) return;
    try {
      const { data } = await api.post(`/owners`, { label: lbl });
      await refreshOwners();
      setForm((f) => ({ ...f, rental_type: data.key, rental_type_custom: "" }));
      setNewOwnerLabel("");
      setShowNewOwner(false);
      toast.success(`Propriétaire ajouté : ${data.label}`);
    } catch (e) {
      toast.error("Impossible d'ajouter le propriétaire");
    }
  };

  const extractFromCarteGrise = async (file) => {
    if (!file) return;
    setExtracting(true);
    setExtractionInfo("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Run OCR extraction AND file upload in parallel so the carte grise is
      // both parsed (VIN → QR code) and attached to the truck's documents.
      const [ocrRes, uploadRes] = await Promise.all([
        api.post("/trucks/extract-from-carte-grise", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        }),
        uploadPhoto(file).catch(() => null),
      ]);
      const d = ocrRes.data || {};
      const carteGrisePath = uploadRes?.path || "";
      if (!d.detected) {
        toast.error("Aucun champ n'a pu être extrait — remplis manuellement.");
        // Still attach the file even if OCR failed
        if (carteGrisePath) {
          setForm((f) => ({ ...f, carte_grise_doc: carteGrisePath }));
        }
        return;
      }
      const brandLc = (d.brand || "").toLowerCase();
      const vanTypeGuess = ["trafic", "master", "jumpy", "expert"].find((k) =>
        (d.model || "").toLowerCase().includes(k) || brandLc.includes(k)
      );
      setForm((f) => ({
        ...f,
        license_plate: d.license_plate || f.license_plate,
        brand: d.brand ? d.brand.charAt(0) + d.brand.slice(1).toLowerCase() : f.brand,
        model: d.model || f.model,
        year: d.year ? String(d.year) : f.year,
        van_type: vanTypeGuess || f.van_type,
        vin: d.vin || f.vin,
        carte_grise_doc: carteGrisePath || f.carte_grise_doc,
      }));
      const filled = [
        d.license_plate && `Immat: ${d.license_plate}`,
        d.brand && `${d.brand}${d.model ? " " + d.model : ""}`,
        d.year && `Année ${d.year}`,
        d.vin && `VIN ${d.vin.slice(0, 6)}…`,
        carteGrisePath && "Document joint",
      ].filter(Boolean).join(" · ");
      setExtractionInfo(filled || "Champs extraits");
      toast.success("Carte grise analysée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur analyse OCR");
    } finally {
      setExtracting(false);
    }
  };

  const submit = async () => {
    if (!form.license_plate.trim()) {
      toast.error("Immatriculation requise");
      return;
    }
    if (plateCheck.exact.length > 0) {
      toast.error("Cette plaque existe déjà");
      return;
    }
    if (plateCheck.similar.length > 0) {
      const list = plateCheck.similar.map((s) => s.license_plate).join(", ");
      const ok = window.confirm(
        `Attention : plaques très similaires détectées : ${list}\n\nÊtes-vous sûr de vouloir ajouter "${form.license_plate}" malgré tout ?`
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year) : null,
        mileage: form.mileage ? parseInt(form.mileage) : null,
        monthly_rent: form.monthly_rent ? parseFloat(form.monthly_rent) : 0,
        daily_revenue: form.daily_revenue ? parseFloat(form.daily_revenue) : 200,
      };
      const t = await createTruck(payload);
      const parts = [`Camion ${t.license_plate} ajouté`];
      if (form.carte_grise_doc) parts.push("carte grise jointe");
      if (form.vin) parts.push("QR code généré");
      toast.success(parts.join(" · "));
      setOpen(false);
      reset();
      onCreated && onCreated(t);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur lors de la création";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => {
      if (v) {
        // On open: sync site with the current dashboard site tab
        setForm((f) => ({ ...f, site: currentSite || "DLP4" }));
      }
      setOpen(v);
      if (!v) {
        reset();
        setPlateCheck({ exact: [], similar: [], loading: false });
      }
    }}>
      <DialogTrigger asChild>
        <button
          className="bg-black text-white px-5 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[var(--ny-gold-deep)] transition-none inline-flex items-center gap-2 brutalist-shadow-sm border border-black"
          data-testid="add-truck-btn"
        >
          <Plus size={16} weight="bold" /> Nouveau camion
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border border-zinc-900 max-w-lg">
        <DialogHeader>
          <DialogTitle
            className="text-2xl font-black uppercase tracking-tight"
            style={{ fontFamily: "Chivo" }}
          >
            Ajouter un camion
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            Renseignez les informations du véhicule pour l&apos;ajouter à votre flotte.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div className="col-span-2 border-2 border-dashed border-zinc-900 bg-[var(--ny-gold)]/10 p-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[180px]">
                <div className="text-[11px] uppercase tracking-widest font-black">
                  📄 Pré-remplir depuis la carte grise
                </div>
                <div className="text-[10px] text-zinc-600 mt-0.5">
                  Immat., marque, modèle, type, année et VIN extraits automatiquement.
                </div>
              </div>
              <label
                className="bg-zinc-900 text-white px-3 py-2 text-[11px] uppercase tracking-widest font-bold cursor-pointer hover:bg-zinc-700 inline-flex items-center gap-2"
                data-testid="cg-extract-label"
              >
                {extracting ? "Analyse..." : "Choisir la carte grise"}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => extractFromCarteGrise(e.target.files?.[0])}
                  className="hidden"
                  disabled={extracting}
                  data-testid="cg-extract-input"
                />
              </label>
            </div>
            {extractionInfo && (
              <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-800 font-bold" data-testid="cg-extract-result">
                ✓ {extractionInfo}
              </div>
            )}
          </div>
          <div className="col-span-2">
            <Label className="label-tag">Immatriculation *</Label>
            <Input
              data-testid="input-license-plate"
              className={`rounded-none mono uppercase ${
                plateCheck.exact.length > 0
                  ? "border-red-600 ring-2 ring-red-200"
                  : plateCheck.similar.length > 0
                  ? "border-amber-500 ring-2 ring-amber-100"
                  : "border-zinc-900"
              }`}
              value={form.license_plate}
              onChange={(e) =>
                setForm({ ...form, license_plate: e.target.value.toUpperCase() })
              }
              placeholder="AB-123-CD"
            />
            {plateCheck.loading && (
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1" data-testid="plate-check-loading">
                Vérification…
              </div>
            )}
            {!plateCheck.loading && plateCheck.exact.length > 0 && (
              <div
                className="mt-1.5 px-2 py-1.5 border border-red-600 bg-red-50 text-red-900 text-xs"
                data-testid="plate-check-exact"
              >
                <div className="font-bold uppercase tracking-widest text-[10px] mb-1">
                  ⛔ Plaque déjà utilisée
                </div>
                {plateCheck.exact.map((d) => (
                  <div key={d.truck_id} className="mono">
                    <span className="font-bold">{d.license_plate}</span> · {(d.brand || "—").toUpperCase()} · {d.site || "—"} · {d.status}
                  </div>
                ))}
              </div>
            )}
            {!plateCheck.loading && plateCheck.exact.length === 0 && plateCheck.similar.length > 0 && (
              <div
                className="mt-1.5 px-2 py-1.5 border border-amber-500 bg-amber-50 text-amber-900 text-xs"
                data-testid="plate-check-similar"
              >
                <div className="font-bold uppercase tracking-widest text-[10px] mb-1">
                  ⚠ Plaque(s) très similaire(s) — risque de doublon
                </div>
                {plateCheck.similar.map((d) => (
                  <div key={d.truck_id} className="mono">
                    <span className="font-bold">{d.license_plate}</span> · {(d.brand || "—").toUpperCase()} · {d.site || "—"} ·{" "}
                    <span className="opacity-70">{Math.round(d.similarity * 100)}% match</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="label-tag">Marque</Label>
            <Input
              data-testid="input-brand"
              className="rounded-none border-zinc-900"
              value={form.brand}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
              placeholder="Renault"
            />
          </div>
          <div>
            <Label className="label-tag">Modèle</Label>
            <Input
              data-testid="input-model"
              className="rounded-none border-zinc-900"
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              placeholder="Trafic"
            />
          </div>
          <div>
            <Label className="label-tag">Type véhicule</Label>
            <Select
              value={form.van_type}
              onValueChange={(v) => setForm({ ...form, van_type: v })}
            >
              <SelectTrigger
                className="rounded-none border-zinc-900"
                data-testid="select-van-type"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="trafic">Renault Trafic</SelectItem>
                <SelectItem value="expert">Peugeot Expert</SelectItem>
                <SelectItem value="jumpy">Citroën Jumpy</SelectItem>
                <SelectItem value="vivaro">Opel Vivaro</SelectItem>
                <SelectItem value="transit">Ford Transit</SelectItem>
                <SelectItem value="generic">Générique 6m³</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tag">Année</Label>
            <Input
              data-testid="input-year"
              type="number"
              className="rounded-none border-zinc-900"
              value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value })}
              placeholder="2022"
            />
          </div>
          <div>
            <Label className="label-tag">Kilométrage</Label>
            <Input
              data-testid="input-mileage"
              type="number"
              className="rounded-none border-zinc-900"
              value={form.mileage}
              onChange={(e) => setForm({ ...form, mileage: e.target.value })}
              placeholder="45000"
            />
          </div>
          <div>
            <Label className="label-tag">Site</Label>
            <Select
              value={form.site}
              onValueChange={(v) => setForm({ ...form, site: v })}
            >
              <SelectTrigger className="rounded-none border-zinc-900" data-testid="input-site">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                {sitesList.map((s) => (
                  <SelectItem key={s.code} value={s.code}>
                    {s.name ? `${s.name} ${s.code}` : s.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tag">Propriété</Label>
            <div className="flex gap-2">
              <Select
                value={form.rental_type}
                onValueChange={(v) => setForm({ ...form, rental_type: v })}
              >
                <SelectTrigger
                  className="rounded-none border-zinc-900"
                  data-testid="input-rental-type"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {owners.map((o) => (
                    <SelectItem key={o.key} value={o.key}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                onClick={() => setShowNewOwner((v) => !v)}
                className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-900 bg-white hover:bg-zinc-900 hover:text-white whitespace-nowrap"
                data-testid="toggle-new-owner"
              >
                + Nouveau
              </button>
            </div>
          </div>
          {showNewOwner && (
            <div className="col-span-2 bg-zinc-50 border border-zinc-900 p-3">
              <Label className="label-tag">Nouveau propriétaire</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={newOwnerLabel}
                  onChange={(e) => setNewOwnerLabel(e.target.value)}
                  placeholder="Ex : UTPF, Sixt, Avis…"
                  className="rounded-none border-zinc-900"
                  data-testid="input-new-owner"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddOwner();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddOwner}
                  disabled={!newOwnerLabel.trim()}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-900 bg-zinc-900 text-white disabled:opacity-40 whitespace-nowrap"
                  data-testid="confirm-new-owner"
                >
                  Ajouter
                </button>
              </div>
            </div>
          )}
          {/* ---- Finance / Budget ---- */}
          <div className="col-span-2 mt-2 border-t border-dashed border-zinc-300 pt-3">
            <Label className="label-tag block mb-2">Finance</Label>
            <div className="grid grid-cols-2 gap-4">
              {form.rental_type !== "own" && (
                <>
                  <div className="col-span-2">
                    <Label className="label-tag">Contact agence (email / téléphone)</Label>
                    <Input
                      data-testid="input-rental-agency"
                      className="rounded-none border-zinc-900"
                      value={form.rental_agency}
                      onChange={(e) => setForm({ ...form, rental_agency: e.target.value })}
                      placeholder="contact@sixt.fr ou 01 23 45 67 89"
                    />
                  </div>
                  <div>
                    <Label className="label-tag">Loyer mensuel (€) *</Label>
                    <Input
                      data-testid="input-monthly-rent"
                      type="number"
                      className="rounded-none border-zinc-900"
                      value={form.monthly_rent}
                      onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })}
                      placeholder="800"
                    />
                  </div>
                </>
              )}
              <div className={form.rental_type !== "own" ? "" : "col-span-2"}>
                <Label className="label-tag">CA journalier (€/jour)</Label>
                <Input
                  data-testid="input-daily-revenue"
                  type="number"
                  className="rounded-none border-zinc-900"
                  value={form.daily_revenue}
                  onChange={(e) => setForm({ ...form, daily_revenue: e.target.value })}
                  placeholder="200"
                />
                <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">
                  Ce que rapporte 1 tournée · utilisé pour calculer le manque à gagner en cas de panne
                </div>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            className="rounded-none border-zinc-900"
            data-testid="cancel-truck-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || plateCheck.exact.length > 0}
            className="rounded-none bg-zinc-900 text-white hover:bg-zinc-700 disabled:opacity-40"
            data-testid="confirm-truck-btn"
          >
            {submitting ? "Ajout..." : plateCheck.exact.length > 0 ? "Doublon bloqué" : "Créer le camion"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { sites } = useSites();
  const [trucks, setTrucks] = useState([]);
  const [stats, setStats] = useState(null);
  const [allImpacts, setAllImpacts] = useState([]);
  const [owners, setOwners] = useState([]);
  const [documentAlerts, setDocumentAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [driverFilter, setDriverFilter] = useState("");
  const [minCost, setMinCost] = useState("");
  const [maxCost, setMaxCost] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [site, setSite] = useState(() => {
    try {
      return localStorage.getItem("flotteops_site") || "DLP4";
    } catch {
      return "DLP4";
    }
  });

  // Once sites are loaded, if the stored site is no longer valid, fall back to the first available.
  useEffect(() => {
    if (sites.length > 0 && !sites.some((s) => s.code === site)) {
      const fallback = sites[0].code;
      setSite(fallback);
      try { localStorage.setItem("flotteops_site", fallback); } catch { /* noop */ }
    }
     
  }, [sites]);

  const switchSite = (s) => {
    setSite(s);
    try {
      localStorage.setItem("flotteops_site", s);
    } catch (err) {
      // localStorage may be unavailable (private mode, quota exceeded) — non-blocking
      console.warn("Could not persist site selection:", err);
    }
  };

  const refreshOwners = async () => {
    try {
      const { data } = await api.get(`/owners`);
      setOwners(data);
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
         
        console.warn("owners refresh failed:", e);
      }
    }
  };

  const ownerMap = React.useMemo(() => {
    const m = {};
    for (const o of owners) m[o.key] = o.label;
    return m;
  }, [owners]);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      api.get(`/impacts/summary?site=${site}`)
        .then((r) => setAllImpacts(r.data))
        .catch(() => {});
      const [t, s] = await Promise.all([
        api.get(`/trucks?site=${site}`).then((r) => r.data),
        api.get(`/stats?site=${site}`).then((r) => r.data).catch(() => null),
      ]);
      setTrucks(t);
      if (s) setStats(s);
    } catch (e) {
      if (!silent) toast.error("Erreur de chargement");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    refreshOwners();
    (async () => {
      try {
        const { data } = await api.get(`/alerts/documents`);
        setDocumentAlerts(data);
      } catch (e) {
        if (process.env.NODE_ENV === "development") {
           
          console.warn("doc alerts failed:", e);
        }
      }
    })();
     
  }, [site]);

  // Silent auto-refresh: polls every 20s + on tab focus, without spinner/toast
  useAutoRefresh(() => load({ silent: true }), 20000);

  // Per-truck aggregated info
  const truckAggregates = React.useMemo(() => {
    const map = {};
    for (const imp of allImpacts) {
      if (!map[imp.truck_id]) {
        map[imp.truck_id] = {
          count: 0,
          total_cost: 0,
          drivers: new Set(),
          last_date: null,
        };
      }
      const agg = map[imp.truck_id];
      agg.count++;
      agg.total_cost += imp.cost || 0;
      if (imp.driver) agg.drivers.add(imp.driver);
      if (!agg.last_date || imp.date > agg.last_date) agg.last_date = imp.date;
    }
    return map;
  }, [allImpacts]);

  const allDrivers = React.useMemo(() => {
    const s = new Set();
    for (const i of allImpacts) if (i.driver) s.add(i.driver);
    return Array.from(s).sort();
  }, [allImpacts]);

  const filtered = trucks.filter((t) => {
    const q = query.trim().toLowerCase();
    const agg = truckAggregates[t.id] || { count: 0, total_cost: 0, drivers: new Set(), last_date: null };

    const matchQ =
      !q ||
      t.license_plate?.toLowerCase().includes(q) ||
      t.brand?.toLowerCase().includes(q) ||
      t.model?.toLowerCase().includes(q) ||
      Array.from(agg.drivers).some((d) => d.toLowerCase().includes(q));

    const matchF =
      (filter === "all" && t.status !== "rendu" && t.status !== "inactif") ||
      (filter === "red_tag" && t.red_tag) ||
      (filter === "expert" && t.awaiting_expert) ||
      filter === t.status;

    const truckOwnerLabel =
      t.rental_type === "autre" && t.rental_type_custom
        ? t.rental_type_custom
        : ownerMap[t.rental_type || "own"] ||
          RENTAL_LABEL[t.rental_type || "own"] ||
          t.rental_type ||
          "—";

    let ownerFilterLabel = null;
    if (ownerFilter !== "all") {
      const matchInOwners = owners.find((o) => o.key === ownerFilter);
      ownerFilterLabel = matchInOwners
        ? matchInOwners.label
        : RENTAL_LABEL[ownerFilter] || ownerFilter;
    }
    const matchOwner =
      ownerFilter === "all" ||
      (ownerFilterLabel &&
        truckOwnerLabel.toLowerCase() === ownerFilterLabel.toLowerCase());

    const matchDriver =
      !driverFilter || agg.drivers.has(driverFilter);

    const minOK = !minCost || agg.total_cost >= parseFloat(minCost);
    const maxOK = !maxCost || agg.total_cost <= parseFloat(maxCost);

    let dateOK = true;
    if (dateFrom || dateTo) {
      if (!agg.last_date) dateOK = false;
      else {
        const ld = new Date(agg.last_date);
        if (dateFrom && ld < new Date(dateFrom)) dateOK = false;
        if (dateTo) {
          const dt = new Date(dateTo);
          dt.setHours(23, 59, 59);
          if (ld > dt) dateOK = false;
        }
      }
    }

    return matchQ && matchF && matchOwner && matchDriver && minOK && maxOK && dateOK;
  }).sort((a, b) =>
    (a.license_plate || "").localeCompare(b.license_plate || "", "fr", { sensitivity: "base", numeric: true })
  );

  const clearAdvanced = () => {
    setDriverFilter("");
    setMinCost("");
    setMaxCost("");
    setDateFrom("");
    setDateTo("");
  };

  const advancedActive =
    driverFilter || minCost || maxCost || dateFrom || dateTo;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-8 py-8 paper-bg min-h-screen" data-testid="dashboard-page">
      {/* Hero */}
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-tag">Inspection · Sinistres · Flotte</div>
          <h1 className="serif-display text-5xl md:text-7xl mt-2 leading-none">
            Tableau de bord
          </h1>
          <div className="ny-divider w-40 mt-3" />
          <p className="text-zinc-700 mt-3 max-w-xl">
            Contrôlez l&apos;état de votre flotte, marquez les impacts en 3D et suivez les
            sinistres en temps réel.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportFleetExcelButton />
          <AddTruckDialog onCreated={load} currentSite={site} owners={owners} refreshOwners={refreshOwners} />
        </div>
      </div>

      {/* Site tabs */}
      <div className="flex gap-0 mb-6 flex-wrap" data-testid="site-tabs">
        {sites.map((s) => (
          <button
            key={s.code}
            onClick={() => switchSite(s.code)}
            className={`px-5 py-3 text-xs uppercase tracking-widest font-bold border border-zinc-900 inline-flex items-center gap-2 transition-none ${
              site === s.code
                ? "bg-zinc-900 text-white"
                : "bg-white text-zinc-900 hover:bg-zinc-100 border-b-2"
            }`}
            data-testid={`site-tab-${s.code}`}
          >
            <MapPin size={14} weight="bold" /> {s.name ? `${s.name} ${s.code}` : s.code}
          </button>
        ))}
      </div>

      {/* Documents alerts banner */}
      {documentAlerts.length > 0 && (
        <div
          className="mb-5 border border-zinc-900 bg-white"
          data-testid="document-alerts-banner"
        >
          <div className="flex items-center justify-between bg-zinc-900 text-white px-3 py-2">
            <div className="flex items-center gap-2">
              <Warning size={16} weight="fill" className="text-amber-400" />
              <div className="text-xs font-bold uppercase tracking-widest">
                Alertes documents · {documentAlerts.length}
              </div>
            </div>
            <div className="text-[10px] font-mono uppercase opacity-80">
              {documentAlerts.filter((a) => a.level === "expired").length} expirés ·{" "}
              {documentAlerts.filter((a) => a.level !== "expired").length} à venir
            </div>
          </div>
          <div className="divide-y divide-zinc-200">
            {documentAlerts.slice(0, 5).map((a, i) => (
              <button
                key={`${a.truck_id}-${a.field}`}
                onClick={() => navigate(`/trucks/${a.truck_id}`)}
                className="w-full flex flex-wrap items-center justify-between gap-2 px-3 py-2 hover:bg-zinc-50 text-left"
                data-testid={`doc-alert-${i}`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                      a.level === "expired"
                        ? "bg-red-600 text-white"
                        : a.level === "urgent"
                        ? "bg-orange-600 text-white"
                        : "bg-amber-400 text-zinc-900"
                    }`}
                  >
                    {a.level === "expired"
                      ? `Expiré · ${Math.abs(a.days_left)}j`
                      : `${a.days_left}j`}
                  </span>
                  <span className="font-bold mono uppercase">{a.license_plate}</span>
                  <span className="text-xs text-zinc-600">{a.label}</span>
                </div>
                <span className="text-xs mono text-zinc-500">
                  {a.expiry} →
                </span>
              </button>
            ))}
            {documentAlerts.length > 5 && (
              <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                + {documentAlerts.length - 5} autre(s)
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          <StatCard
            label="Flotte active"
            value={stats.total_active}
            testId="stat-total"
          />
          <StatCard
            label="Inactifs"
            value={stats.inactif}
            accent="#71717A"
            testId="stat-inactif"
          />
          <StatCard
            label="Rendus"
            value={stats.rendu}
            accent="#2563EB"
            testId="stat-rendu"
          />
          <StatCard
            label="En panne"
            value={stats.panne}
            accent="#D97706"
            testId="stat-panne"
          />
          <StatCard
            label="Red Tag"
            value={stats.red_tag}
            accent="#DC2626"
            testId="stat-red-tag"
          />
          <StatCard
            label="Att. expert"
            value={stats.awaiting_expert}
            accent="#7C3AED"
            testId="stat-expert"
          />
        </div>
      )}

      {/* Search + Filter */}
      <div className="bg-white border border-zinc-900 mb-1 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass
              size={16}
              weight="bold"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
            />
            <input
              type="text"
              placeholder="Rechercher immatriculation, marque, chauffeur…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-zinc-300 bg-white focus:border-zinc-900 outline-none font-mono text-sm"
              data-testid="search-trucks"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {[
              { v: "all", l: "Tous" },
              { v: "actif", l: "Actifs" },
              { v: "inactif", l: "Inactifs" },
              { v: "rendu", l: "Rendus" },
              { v: "panne", l: "En panne" },
              { v: "red_tag", l: "Red Tag" },
              { v: "expert", l: "Att. expert" },
            ].map((f) => (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border transition-none ${
                  filter === f.v
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
                }`}
                data-testid={`filter-${f.v}`}
              >
                {f.l}
              </button>
            ))}
          </div>
          <button
            onClick={() => setAdvancedOpen((v) => !v)}
            className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border inline-flex items-center gap-2 transition-none ${
              advancedActive || advancedOpen
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
            }`}
            data-testid="toggle-advanced-filters"
          >
            <FunnelSimple size={14} weight="bold" />
            Filtres avancés{advancedActive ? " · ON" : ""}
          </button>
        </div>

        {/* Owner filter row */}
        <div
          className="flex flex-wrap gap-1.5 pt-3 border-t border-zinc-200"
          data-testid="owner-filter-row"
        >
          <div className="label-tag self-center mr-2">Propriété</div>
          <button
            onClick={() => setOwnerFilter("all")}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border transition-none ${
              ownerFilter === "all"
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
            }`}
            data-testid="owner-filter-all"
          >
            Tous
          </button>
          {(() => {
            // Group owners by lower-cased label so legacy "autre" + new key merge
            const groups = {};
            for (const o of owners) {
              const k = (o.label || o.key).trim().toLowerCase();
              if (!groups[k]) groups[k] = { label: o.label, keys: [] };
              groups[k].keys.push(o.key);
            }
            // Also include any rental_type still attached to trucks (e.g. "autre")
            for (const t of trucks) {
              const rt = t.rental_type || "own";
              const lbl =
                t.rental_type === "autre" && t.rental_type_custom
                  ? t.rental_type_custom
                  : ownerMap[rt] || RENTAL_LABEL[rt] || rt;
              const k = (lbl || rt).trim().toLowerCase();
              if (!groups[k]) groups[k] = { label: lbl, keys: [] };
              if (!groups[k].keys.includes(rt)) groups[k].keys.push(rt);
            }
            return Object.values(groups)
              .map((g) => {
                const groupTrucks = trucks.filter((t) =>
                  g.keys.includes(t.rental_type || "own")
                );
                // Exclude "rendu" trucks from the total count (they're no longer in the fleet)
                const count = groupTrucks.filter(
                  (t) => t.status !== "rendu"
                ).length;
                // Pannes count for this owner
                const panneCount = groupTrucks.filter(
                  (t) => t.status === "panne"
                ).length;
                return { ...g, count, panneCount };
              })
              .filter((g) => g.count > 0)
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((g) => {
                const isActive = g.keys.includes(ownerFilter);
                return (
                  <button
                    key={g.label}
                    onClick={() =>
                      setOwnerFilter(isActive ? "all" : g.keys[0])
                    }
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border inline-flex items-center gap-1.5 transition-none ${
                      isActive
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-900 border-zinc-300 hover:border-zinc-900"
                    }`}
                    data-testid={`owner-filter-${g.keys[0]}`}
                    title={
                      g.panneCount > 0
                        ? `${g.count} camion(s) — ${g.panneCount} en panne`
                        : `${g.count} camion(s)`
                    }
                  >
                    {g.label}
                    <span
                      className={`px-1.5 text-[10px] mono ${
                        isActive
                          ? "bg-white text-zinc-900"
                          : "bg-zinc-200 text-zinc-700"
                      }`}
                    >
                      {g.count}
                    </span>
                    {g.panneCount > 0 && (
                      <span
                        className="px-1.5 text-[10px] mono bg-amber-500 text-white inline-flex items-center gap-0.5"
                        data-testid={`owner-panne-${g.keys[0]}`}
                        title={`${g.panneCount} en panne`}
                      >
                        ⚠ {g.panneCount}
                      </span>
                    )}
                  </button>
                );
              });
          })()}
        </div>

        {advancedOpen && (
          <div
            className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-3 border-t border-zinc-200"
            data-testid="advanced-filters-panel"
          >
            <div>
              <div className="label-tag mb-1">Chauffeur</div>
              <Select
                value={driverFilter || "__all__"}
                onValueChange={(v) => setDriverFilter(v === "__all__" ? "" : v)}
              >
                <SelectTrigger
                  className="rounded-none border-zinc-900 h-9"
                  data-testid="filter-driver"
                >
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="__all__">Tous</SelectItem>
                  {allDrivers.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="label-tag mb-1">Coût min (€)</div>
              <Input
                type="number"
                value={minCost}
                onChange={(e) => setMinCost(e.target.value)}
                placeholder="0"
                className="rounded-none border-zinc-900 h-9 mono"
                data-testid="filter-min-cost"
              />
            </div>
            <div>
              <div className="label-tag mb-1">Coût max (€)</div>
              <Input
                type="number"
                value={maxCost}
                onChange={(e) => setMaxCost(e.target.value)}
                placeholder="∞"
                className="rounded-none border-zinc-900 h-9 mono"
                data-testid="filter-max-cost"
              />
            </div>
            <div>
              <div className="label-tag mb-1">Impact depuis</div>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="rounded-none border-zinc-900 h-9 mono"
                data-testid="filter-date-from"
              />
            </div>
            <div>
              <div className="label-tag mb-1">Impact jusqu&apos;à</div>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="rounded-none border-zinc-900 h-9 mono"
                data-testid="filter-date-to"
              />
            </div>
            {advancedActive && (
              <div className="col-span-2 md:col-span-5">
                <button
                  onClick={clearAdvanced}
                  className="text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1 hover:underline"
                  data-testid="clear-advanced-filters"
                >
                  <X size={12} weight="bold" /> Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Truck list */}
      <div className="border border-zinc-900 bg-zinc-100" data-testid="truck-list">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-zinc-900 text-white">
          <div className="col-span-12 md:col-span-3 label-tag !text-zinc-400">
            Véhicule
          </div>
          <div className="col-span-6 md:col-span-2 label-tag !text-zinc-400">Type</div>
          <div className="col-span-6 md:col-span-2 label-tag !text-zinc-400">
            Propriété
          </div>
          <div className="col-span-6 md:col-span-2 label-tag !text-zinc-400">KM</div>
          <div className="col-span-6 md:col-span-2 label-tag !text-zinc-400">Statut</div>
          <div className="col-span-12 md:col-span-1"></div>
        </div>
        {loading && (
          <div className="p-8 text-center text-zinc-500" data-testid="loading">
            Chargement de la flotte…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div
            className="p-12 text-center bg-white"
            data-testid="empty-state"
          >
            <TruckIcon size={48} weight="duotone" className="mx-auto text-zinc-300 mb-3" />
            <div className="text-lg font-bold uppercase">Aucun camion</div>
            <div className="text-zinc-500 mt-1">
              Commencez par ajouter votre premier véhicule.
            </div>
          </div>
        )}
        {!loading &&
          filtered.map((t) => (
            <TruckRow
              key={t.id}
              truck={t}
              agg={truckAggregates[t.id]}
              ownerMap={ownerMap}
              onOpen={() => navigate(`/trucks/${t.id}`)}
            />
          ))}
      </div>
    </div>
  );
}
